import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps } from '../general';
import { loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData } from './dataBackend';

export type UserDb = {
  mail: string;
  name: string;
  language: string;
  registrationDateTime: string;
}
const initial: UserDb = {
  mail: '',
  name: '',
  language: '',
  registrationDateTime: '',
};

export type UserDbHashType = {
  [key in string]: UserDb;
}

export type UserAddPayload = {
  id?: string;
} & UserDb;
export type UserUpdatePayload = {
  id: string;
} & { [P in keyof UserDb]?: UserDb[P] };
export type UserSignUpPayload = {
  id: string;
  mail: string;
  name: string;
  language?: string;
  registrationDateTime: string;
}

export type UserDbState = {
  users: UserDbHashType;
  currentUserId: string | null;
}

export const generateUserId = (): string => generateId('u');
let initialState: UserDbState = { users: {}, currentUserId: null };
const key = 'users';

const userDbSlice = createSlice({
  name: 'userDb',
  initialState,
  reducers: {
    initializeUsers(state: UserDbState) {
      state.users = {};
      state.currentUserId = null;
    },
    addUser(state: UserDbState, action: PayloadAction<UserAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateUserId();
      state.users[id] = obj;
      saveData(key, id, obj);
    },
    updateUser(state: UserDbState, action: PayloadAction<UserUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.users[id] === undefined) {
        console.log(`*** User not found: ${id}`);
      } else {
        const newData = { ...state.users[id], ...obj };
        state.users[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteUser(state: UserDbState, action: PayloadAction<UserUpdatePayload>) {
      const id = action.payload.id;
      delete state.users[id];
      deleteData(key, id);
    },
    saveUsers(state: UserDbState) {
      saveAllData(key, state.users);
    },
    setCurrentUser(state: UserDbState, action: PayloadAction<string>) {
      state.currentUserId = action.payload;
    },
    signUpUser(state: UserDbState, action: PayloadAction<UserSignUpPayload>) {
      const { id, ...obj } = action.payload;
      const currentUser = state.currentUserId ? state.users[state.currentUserId] : initial;
      const userToSignUp = { ...currentUser, ...obj };
      state.users[id] = userToSignUp;
      saveData(key, id, userToSignUp);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: UserDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.users = updatedValues;
      if (changed) saveAllData(key, state.users);
    })
  }
})

export const { initializeUsers, addUser, updateUser, deleteUser, saveUsers, setCurrentUser, signUpUser } = userDbSlice.actions;
export default userDbSlice.reducer;
